import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// Presentation-only: section icons + display order fallback. The API remains
// authoritative for labels, descriptions, fields, values, statuses and visibility.
const SECTION_ICONS = {
  personal: "User", household: "Users", residency: "MapPin", filing_history: "FileText",
  income: "Wallet", international: "Globe", thailand: "Landmark",
};
const SECTION_ORDER = ["personal", "household", "residency", "filing_history", "income", "international", "thailand"];

// Client-safe status appearance map (text + muted/subdued treatment; never alarm-heavy).
const STATUS_STYLE = {
  Confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Needs Review": "bg-amber-50 text-amber-700 border-amber-200",
  "Partially Complete": "bg-sky-50 text-sky-700 border-sky-200",
  "Not Provided": "bg-slate-100 text-slate-500 border-slate-200",
};
const STATUS_ICON = {
  Confirmed: "CheckCircle2", "Needs Review": "AlertCircle",
  "Partially Complete": "CircleDot", "Not Provided": "Circle",
};

// Deterministic section rollup from client-safe field statuses returned by the API.
function sectionStatus(fields) {
  const s = (fields || []).map((f) => f.status);
  if (!s.length) return "Not Provided";
  const needsReview = s.some((x) => x === "needs_followup" || x === "pending_client");
  if (needsReview) return "Needs Review";
  const provided = s.filter((x) => x === "answered" || x === "staff_verified" || x === "not_applicable").length;
  const unknown = s.filter((x) => x === "unknown").length;
  if (provided === s.length) return "Confirmed";
  if (provided === 0) return "Not Provided";
  return "Partially Complete";
}

function StatusBadge({ label, size = "sm" }) {
  const Ic = Icons[STATUS_ICON[label] || "Circle"];
  const cls = STATUS_STYLE[label] || STATUS_STYLE["Not Provided"];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium ${cls} ${size === "sm" ? "text-xs" : "text-[11px]"}`}
      data-testid="tax-profile-status-badge">
      <Ic className="h-3.5 w-3.5" aria-hidden="true" />
      {label}
    </span>
  );
}

function FieldRow({ field, onEdit, rowRef }) {
  const notProvided = field.display_value === "Not provided" || field.value == null;
  const pending = field.pending_change;
  const isDerived = field.derived;

  return (
    <div className="py-2.5 flex items-start justify-between gap-3" data-testid={`tax-profile-field-${field.key}`} ref={rowRef}>
      <div className="min-w-0">
        <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide">{field.label}</dt>
        <dd className={`mt-0.5 text-sm ${notProvided ? "text-slate-400 italic" : "text-[#171B2E] font-medium"}`}>
          {field.display_value}
        </dd>
        {pending ? (
          <div className="mt-1 inline-flex items-center gap-1.5 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] text-amber-700"
            data-testid={`tax-profile-pending-${field.key}`}>
            <Icons.Clock className="h-3 w-3" /> Submitted for review: {pending.proposed_display_value}
          </div>
        ) : isDerived ? (
          <div className="mt-0.5 text-[11px] text-slate-400 italic">Determined from your address and citizenship</div>
        ) : field.source_label && !notProvided ? (
          <div className="mt-0.5 text-[11px] text-slate-400">{field.source_label}</div>
        ) : null}
      </div>
      {field.client_can_edit && !pending && (
        <button onClick={() => onEdit(field)} data-testid={`tax-profile-edit-${field.key}`}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#171B2E] border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-slate-50">
          {notProvided
            ? <><Icons.Plus className="h-3 w-3" /> Add</>
            : <><Icons.Pencil className="h-3 w-3" /> Change</>}
        </button>
      )}
    </div>
  );
}

// Address input rendered as structured sub-fields within EditFieldModal.
function AddressEditor({ value, onChange }) {
  const update = (key, val) => onChange({ ...value, [key]: val });
  const cls = "w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40";
  return (
    <div className="space-y-2">
      <input value={value.line1 || ""} onChange={(e) => update("line1", e.target.value)}
        placeholder="Address line 1 *" data-testid="tax-profile-address-line1" className={cls} />
      <input value={value.line2 || ""} onChange={(e) => update("line2", e.target.value)}
        placeholder="Address line 2 (optional)" data-testid="tax-profile-address-line2" className={cls} />
      <div className="grid grid-cols-2 gap-2">
        <input value={value.city || ""} onChange={(e) => update("city", e.target.value)}
          placeholder="City" data-testid="tax-profile-address-city" className={cls} />
        <input value={value.state || ""} onChange={(e) => update("state", e.target.value)}
          placeholder="State / Province" data-testid="tax-profile-address-state" className={cls} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input value={value.postal_code || ""} onChange={(e) => update("postal_code", e.target.value)}
          placeholder="Postal code" data-testid="tax-profile-address-postal" className={cls} />
        <input value={value.country || ""} onChange={(e) => update("country", e.target.value)}
          placeholder="Country *" data-testid="tax-profile-address-country" className={cls} />
      </div>
    </div>
  );
}

const EMPTY_ADDRESS = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "" };

// Fetch the canonical country list once per session from /api/portal/countries.
function useCountries() {
  const [countries, setCountries] = useState([]);
  useEffect(() => {
    portalApi.get("/portal/countries").then(({ data }) => setCountries(data.countries || [])).catch(() => {});
  }, []);
  return countries;
}

// Fetch the canonical IANA timezone list once per session from /api/portal/timezones.
function useTimezones() {
  const [timezones, setTimezones] = useState([]);
  useEffect(() => {
    portalApi.get("/portal/timezones").then(({ data }) => setTimezones(data.timezones || [])).catch(() => {});
  }, []);
  return timezones;
}

// Parse stored timezone value: accepts both "Asia/Bangkok" and "Asia/Bangkok (ICT, UTC+7)"
function parseTimezoneId(stored) {
  if (!stored) return "";
  const clean = stored.trim();
  // If compound format ("Asia/Bangkok (...)"), extract IANA ID portion
  const m = clean.match(/^([A-Za-z_]+\/[A-Za-z_/]+)/);
  return m ? m[1] : clean;
}

// Single-country selector used for country_of_residence.
function CountrySingleSelector({ value, onChange, countries }) {
  const [search, setSearch] = useState(value || "");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const filtered = search.length > 0
    ? countries.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())).slice(0, 60)
    : countries.slice(0, 80);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const select = (name) => { setSearch(name); onChange(name); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      <input value={search} onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} placeholder="Search country…" data-testid="tax-profile-country-single"
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40" />
      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg">
          {filtered.map((c) => (
            <li key={c.code} onMouseDown={() => select(c.name)}
              className="px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer">{c.name}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Multi-country selector used for citizenship (supports dual-citizenship).
function CountryMultiSelector({ value, onChange, countries }) {
  // value is a comma-separated string like "United States, Thailand" (or "United States and Thailand")
  const parseSelections = (raw) => {
    if (!raw) return [];
    return (raw || "").split(/,\s*| and /).map((s) => s.trim()).filter(Boolean);
  };
  const [selected, setSelected] = useState(() => parseSelections(value));
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const filtered = search.length > 0
    ? countries.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())).slice(0, 60)
    : countries.slice(0, 80);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const addCountry = (name) => {
    if (selected.includes(name)) { setSearch(""); setOpen(false); return; }
    const next = [...selected, name];
    setSelected(next);
    onChange(next.join(", "));
    setSearch(""); setOpen(false);
  };
  const remove = (name) => {
    const next = selected.filter((s) => s !== name);
    setSelected(next);
    onChange(next.join(", "));
  };

  return (
    <div className="space-y-2" ref={ref}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((name) => (
            <span key={name} className="inline-flex items-center gap-1 bg-slate-100 text-[#171B2E] rounded-lg px-2.5 py-1 text-sm font-medium">
              {name}
              <button onMouseDown={() => remove(name)} type="button" data-testid={`tax-profile-citizenship-remove-${name}`}
                className="text-slate-400 hover:text-slate-600 ml-0.5"><Icons.X className="h-3 w-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input value={search} onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} placeholder={selected.length > 0 ? "Add another country…" : "Search country…"}
          data-testid="tax-profile-country-multi" className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40" />
        {open && filtered.length > 0 && (
          <ul className="absolute z-50 mt-1 w-full max-h-52 overflow-y-auto bg-white border border-slate-200 rounded-xl shadow-lg">
            {filtered.map((c) => (
              <li key={c.code} onMouseDown={() => addCountry(c.name)}
                className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between
                  ${selected.includes(c.name) ? "bg-slate-50 text-slate-400" : "hover:bg-slate-50"}`}>
                {c.name}
                {selected.includes(c.name) && <Icons.Check className="h-3 w-3 text-emerald-500" />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TimezoneSelector({ value, onChange, timezones }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  const currentId = parseTimezoneId(value);
  const currentEntry = timezones.find((z) => z.id === currentId);
  const displayValue = currentEntry ? currentEntry.label : (value || "");

  const filtered = useMemo(() => {
    // Show ALL timezones when no search term — all 599 IANA zones are scrollable.
  // Asia/Bangkok and other positive-offset zones previously dropped by the old
  // slice(0, 80) cap (which only showed negative-offset zones) are now visible.
  // Search still filters client-side by id/label (including the city segment).
  if (!search.trim()) return timezones;
    const q = search.toLowerCase();
    return timezones.filter((z) => z.id.toLowerCase().includes(q) || z.label.toLowerCase().includes(q)).slice(0, 100);
  }, [timezones, search]);

  useEffect(() => {
    function handle(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  return (
    <div className="relative" ref={dropRef}>
      <div
        className="flex items-center gap-2 border border-slate-600 rounded-lg bg-slate-800 px-3 py-2 cursor-pointer"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? (
          <input
            autoFocus
            className="flex-1 bg-transparent text-slate-100 text-sm outline-none placeholder-slate-400"
            placeholder="Search timezone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="timezone-search-input"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={`flex-1 text-sm ${displayValue ? "text-slate-100" : "text-slate-400"}`}>
            {displayValue || "Select timezone…"}
          </span>
        )}
        <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto" data-testid="timezone-dropdown">
          {filtered.length === 0 && <div className="px-3 py-2 text-slate-400 text-sm">No matches</div>}
          {filtered.map((z) => (
            <div
              key={z.id}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-700 transition-colors ${z.id === currentId ? "text-sky-400 font-medium" : "text-slate-200"}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(z.id); setOpen(false); setSearch(""); }}
              data-testid={`tz-option-${z.id.replace(/\//g, "-")}`}
            >
              {z.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VisaTypeSelector({ value, onChange, options }) {
  const optionValues = useMemo(() => new Set((options || []).map((o) => o.value).filter((v) => v !== "Other")), [options]);
  const isOther = !!(value && !optionValues.has(value));
  const [selected, setSelected] = useState(isOther ? "Other" : (value || ""));
  const [otherText, setOtherText] = useState(isOther ? value : "");

  useEffect(() => {
    const nowOther = !!(value && !optionValues.has(value));
    setSelected(nowOther ? "Other" : (value || ""));
    setOtherText(nowOther ? (value || "") : "");
  }, [value, optionValues]);

  const handleSelect = (e) => {
    const sel = e.target.value;
    setSelected(sel);
    if (sel === "Other") onChange(otherText || "");
    else onChange(sel);
  };

  return (
    <div className="space-y-2">
      <select
        value={selected}
        onChange={handleSelect}
        className="w-full bg-slate-800 border border-slate-600 text-slate-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
        data-testid="visa-type-select"
      >
        <option value="">Select visa type…</option>
        {(options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {selected === "Other" && (
        <input
          type="text"
          value={otherText}
          onChange={(e) => { setOtherText(e.target.value); onChange(e.target.value); }}
          placeholder="Please specify your visa type"
          className="w-full bg-slate-800 border border-slate-600 text-slate-100 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-sky-500"
          data-testid="visa-type-other-input"
        />
      )}
    </div>
  );
}

// ── DependentsControl ─────────────────────────────────────────────────────────
// Two-step controlling question: Yes/No → if Yes, count picker.
// Maps cleanly to canonical count storage: "0"=No, "1"/"2"/...=Yes+count.
// Unset value shows neither Yes nor No selected (unanswered state preserved).
function DependentsControl({ value, onChange }) {
  const isYes = !!(value && value !== "0");
  const isNo = value === "0";
  const [choice, setChoice] = useState(isYes ? "yes" : isNo ? "no" : "");
  const [count, setCount] = useState(isYes ? value : "1");

  const handleChoice = (c) => {
    setChoice(c);
    if (c === "no") onChange("0");
    else if (c === "yes") onChange(count || "1");
  };

  const handleCount = (e) => {
    const n = e.target.value;
    setCount(n);
    onChange(n);
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">Do you have any dependents?</p>
        <div className="flex gap-3">
          <button type="button" onClick={() => handleChoice("yes")}
            className={`flex-1 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
              choice === "yes" ? "bg-[#F5A623] border-[#F5A623] text-white shadow-sm" : "border-slate-300 text-slate-600 hover:border-[#F5A623] hover:text-[#F5A623]"
            }`}
            data-testid="dependents-yes-btn">
            Yes
          </button>
          <button type="button" onClick={() => handleChoice("no")}
            className={`flex-1 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
              choice === "no" ? "bg-slate-100 border-slate-400 text-slate-700" : "border-slate-300 text-slate-600 hover:border-slate-400"
            }`}
            data-testid="dependents-no-btn">
            No
          </button>
        </div>
      </div>
      {choice === "yes" && (
        <div>
          <p className="text-sm font-medium text-slate-700 mb-2">How many dependents?</p>
          <select value={count} onChange={handleCount}
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40"
            data-testid="dependents-count-select">
            {["1","2","3","4","5+"].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

function EditFieldModal({ field, onClose, onSaved }) {
  const isAddress = field.input_type === "address";
  const isCountryMulti = field.input_type === "country_multi";
  const isCountrySingle = field.input_type === "country_single";
  const isTimezone = field.input_type === "timezone";
  const isVisaType = field.input_type === "visa_type";
  const isDependentsControl = field.input_type === "dependents_control";
  const countries = useCountries();
  const timezones = useTimezones();

  const [value, setValue] = useState(() => {
    if (isAddress) {
      return (field.value && typeof field.value === "object") ? { ...EMPTY_ADDRESS, ...field.value } : { ...EMPTY_ADDRESS };
    }
    return field.value ?? "";
  });
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const proposed = field.update_mode === "proposed";

  const submit = async () => {
    if (isAddress) {
      if (!value.line1?.trim() || !value.country?.trim()) {
        return toast.error("Please enter at least an address line and country.");
      }
    } else if (isCountryMulti) {
      if (!value?.trim()) return toast.error("Please select at least one country.");
    } else if (isCountrySingle) {
      if (!value?.trim()) return toast.error("Please select a country.");
    } else if (isTimezone) {
      if (!value?.trim()) return toast.error("Please select a timezone.");
    } else if (isVisaType) {
      if (!value?.trim()) return toast.error("Please select or specify a visa type.");
    } else if (isDependentsControl) {
      if (value === "" || value == null) return toast.error("Please indicate whether you have dependents.");
    } else {
      if (value === "" || value == null) return toast.error("Please enter a value.");
    }
    setBusy(true);
    try {
      const { data } = await portalApi.post("/portal/tax-profile/changes", {
        changes: [{ field_key: field.key, proposed_value: value, client_comment: comment || undefined }],
        submission_id: `${field.key}-${Date.now()}`,
      });
      const r = (data.results || [])[0] || {};
      if (r.outcome === "saved") toast.success("Saved securely.");
      else if (r.outcome === "submitted_for_review") toast.success("Submitted securely for review.");
      else if (r.outcome === "already_pending") toast.info("A change is already awaiting review.");
      else if (r.outcome === "no_change") toast.info("No change was made.");
      else if (r.outcome === "conflict") toast.error("This information changed. Please refresh and try again.");
      else toast.error("Please check the information entered and try again.");
      onSaved();
      onClose();
    } catch {
      toast.error("We could not submit your change right now. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="tax-profile-edit-modal">
        <DialogHeader>
          <DialogTitle>Update {field.label}</DialogTitle>
          {!isAddress && !isCountryMulti && !isCountrySingle &&
            <DialogDescription>Current value: {field.display_value}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          {isAddress ? (
            <AddressEditor value={value} onChange={setValue} />
          ) : isCountryMulti ? (
            <CountryMultiSelector value={value} onChange={setValue} countries={countries} />
          ) : isCountrySingle ? (
            <CountrySingleSelector value={value} onChange={setValue} countries={countries} />
          ) : isTimezone ? (
            <TimezoneSelector value={value} onChange={setValue} timezones={timezones} />
          ) : isVisaType ? (
            <VisaTypeSelector value={value} onChange={setValue} options={field.options || []} />
          ) : isDependentsControl ? (
            <DependentsControl value={value} onChange={setValue} />
          ) : field.input_type === "select" ? (
            <select value={value} onChange={(e) => setValue(e.target.value)} data-testid="tax-profile-edit-input"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40">
              <option value="">Select…</option>
              {(field.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ) : (
            <input value={value} onChange={(e) => setValue(e.target.value)} data-testid="tax-profile-edit-input"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40" />
          )}
          {proposed && (
            <>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="tax-profile-edit-disclosure">
                This change may affect your tax filings and will be reviewed by your tax team before it becomes part of your official Tax Profile.
              </div>
              <textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 1000))} rows={2}
                placeholder="Add a note for your tax team (optional)" data-testid="tax-profile-edit-comment"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 resize-none" />
            </>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={onClose} className="text-sm text-slate-500 px-3 py-2 hover:text-[#171B2E]">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="tax-profile-edit-submit"
              className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-60 text-[#171B2E] font-semibold rounded-lg px-4 py-2 text-sm">
              {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}
              {proposed ? "Submit for review" : "Save"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Returns the first incomplete client-editable field in a section (no pending, no value).
function firstIncompleteField(fields) {
  return (fields || []).find(
    (f) => f.client_can_edit && !f.pending_change && (f.value == null || f.display_value === "Not provided")
  ) || null;
}

function SectionCard({ section, defaultOpen, onEdit }) {
  const [open, setOpen] = useState(defaultOpen);
  const status = sectionStatus(section.fields);
  const Ic = Icons[SECTION_ICONS[section.key] || "FileText"];
  const panelId = `tax-section-panel-${section.key}`;

  // Refs keyed by field.key so "Complete section" can focus the first incomplete row.
  const fieldRefs = useRef({});

  const firstIncomplete = firstIncompleteField(section.fields);

  const handleCompleteSection = (e) => {
    e.stopPropagation();
    setOpen(true);
    // Slight delay to ensure the panel has expanded before invoking the edit modal.
    setTimeout(() => {
      if (firstIncomplete) onEdit(firstIncomplete);
    }, 80);
  };

  return (
    <section id={`tax-profile-section-${section.key}`}
      className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden scroll-mt-24"
      data-testid={`tax-profile-section-${section.key}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={panelId}
        data-testid={`tax-profile-section-toggle-${section.key}`}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50/70 transition-colors">
        <span className="h-9 w-9 rounded-xl bg-[#F6F7FB] flex items-center justify-center shrink-0">
          <Ic className="h-4 w-4 text-[#171B2E]" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-heading font-semibold text-[#171B2E]">{section.label}</span>
          {section.description && <span className="block text-xs text-slate-500 mt-0.5 truncate">{section.description}</span>}
        </span>
        <StatusBadge label={status} />
        {firstIncomplete && (
          <button
            onClick={handleCompleteSection}
            data-testid={`tax-profile-complete-section-${section.key}`}
            className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-[#F5A623] border border-[#F5A623]/40 rounded-lg px-2.5 py-1 hover:bg-[#F5A623]/5 transition-colors"
          >
            <Icons.PenLine className="h-3 w-3" />
            Complete section
          </button>
        )}
        <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div id={panelId} className="px-5 pb-5 pt-1 border-t border-slate-100">
          {section.key === "household" && (
            <p className="text-xs text-slate-400 italic mb-4" data-testid="household-context-note">
              We'll only ask for spouse and dependent information when it applies to your household.
            </p>
          )}
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {(section.fields || []).map((f) => (
              <FieldRow
                key={f.key}
                field={f}
                onEdit={onEdit}
                rowRef={(el) => { fieldRefs.current[f.key] = el; }}
              />
            ))}
          </dl>
        </div>
      )}
    </section>
  );
}

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return null; }
}

function Skeleton() {
  return (
    <div className="space-y-6 animate-pulse" data-testid="tax-profile-loading">
      <div className="h-8 w-56 bg-slate-200 rounded" />
      <div className="h-20 bg-slate-100 rounded-2xl" />
      {[0, 1, 2].map((i) => <div key={i} className="h-16 bg-white border border-slate-200 rounded-2xl" />)}
    </div>
  );
}

export default function PortalTaxProfile() {
  const [state, setState] = useState({ loading: true, error: false, data: null });
  const [editing, setEditing] = useState(null);
  const location = useLocation();

  const load = useCallback(async () => {
    setState({ loading: true, error: false, data: null });
    try {
      const { data } = await portalApi.get("/portal/tax-profile");
      setState({ loading: false, error: false, data });
    } catch {
      setState({ loading: false, error: true, data: null });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link to the first actionable section (Package 3 Dashboard action / nav badge).
  useEffect(() => {
    if (state.loading || !location.hash) return;
    const el = document.querySelector(location.hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [state.loading, location.hash]);

  if (state.loading) return <Skeleton />;

  if (state.error) {
    return (
      <div className="max-w-md mx-auto text-center py-16" data-testid="tax-profile-error">
        <Icons.CloudOff className="h-10 w-10 text-slate-300 mx-auto mb-3" aria-hidden="true" />
        <p className="text-slate-600">We could not load your Tax Profile right now. Please try again.</p>
        <div className="mt-5 flex items-center justify-center gap-3">
          <button onClick={load} data-testid="tax-profile-retry"
            className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-semibold rounded-lg px-4 py-2 text-sm">
            <Icons.RefreshCw className="h-4 w-4" /> Retry
          </button>
          <Link to="/portal" className="text-sm text-slate-500 hover:text-[#171B2E]">Return to Dashboard</Link>
        </div>
      </div>
    );
  }

  const { data } = state;
  const completion = data.profile_status?.completion || {};
  const totals = {
    answered: completion.answered || 0,
    needs_review: completion.needs_review || 0,
    unknown: completion.unknown || 0,
  };
  const anyData = totals.answered + totals.needs_review > 0;
  const sections = [...(data.sections || [])].sort(
    (a, b) => (SECTION_ORDER.indexOf(a.key) + 100) - (SECTION_ORDER.indexOf(b.key) + 100));
  const lastReviewed = fmtDate(data.profile_status?.last_verified_at);

  return (
    <div className="space-y-6 max-w-4xl" data-testid="portal-tax-profile">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-bold text-[#171B2E]">Tax Profile</h1>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500"
              data-testid="tax-profile-review-note" title="Tax-sensitive changes are reviewed by your tax team before they become official.">
              <Icons.ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> Reviewed by your tax team
            </span>
          </div>
          <p className="text-slate-500 text-sm mt-1">
            Review the personal, household, residency and tax information currently on file — and let us know if anything has changed.
          </p>
        </div>
        <div className="text-right text-xs text-slate-400 pt-1" data-testid="tax-profile-last-reviewed">
          {lastReviewed ? `Last reviewed: ${lastReviewed}` : "Not yet reviewed through the portal"}
        </div>
      </div>

      {anyData ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="tax-profile-summary">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Profile overview</div>
          <div className="flex flex-wrap gap-6">
            <div><div className="text-2xl font-bold text-emerald-600">{totals.answered}</div><div className="text-xs text-slate-500">Provided</div></div>
            <div><div className="text-2xl font-bold text-amber-600">{totals.needs_review}</div><div className="text-xs text-slate-500">Need review</div></div>
            <div><div className="text-2xl font-bold text-slate-400">{totals.unknown}</div><div className="text-xs text-slate-500">Not provided</div></div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 text-center" data-testid="tax-profile-empty">
          <Icons.ClipboardList className="h-9 w-9 text-slate-300 mx-auto mb-3" aria-hidden="true" />
          <p className="text-slate-600 text-sm max-w-md mx-auto">
            Your Tax Profile has not been completed yet. Information you provide through onboarding, your organizer and your tax team will appear here.
          </p>
        </div>
      )}

      {(data.requested_services || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="portal-requested-services">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">You requested</div>
          <ul className="space-y-1.5">
            {data.requested_services.map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[#171B2E]" data-testid={`portal-requested-service-${i}`}>
                <Icons.Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" aria-hidden="true" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-4">
        {sections.map((section) => (
          <SectionCard key={section.key} section={section} onEdit={setEditing}
            defaultOpen={section.key === "personal" || sectionStatus(section.fields) === "Needs Review"} />
        ))}
      </div>

      {editing && <EditFieldModal field={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}
